/** Imports **/
import React, {createRef, Fragment, useEffect, useRef, useState} from 'react';
import cx from 'classnames';
import doNotUseAccessibleStaticElementInteractions from "A11y/doNotUseAccessibleStaticElementInteractions.js";
import ListeningStatus from '../Global/ListeningStatus';
import { listen, abortListen } from 'Services/Listen';
import useCancellablePromise from "../../hooks/useCancellablePromise";
import { AmiraTheme } from "Components/Login/AmiraTheme";
import * as AmplifyUI from "@aws-amplify/ui";
import {
  FormField,
  InputLabel,
} from 'aws-amplify-react/lib/Amplify-UI/Amplify-UI-Components-React.js';
import { Button } from '../Global/Button';
import { logSentryException } from "Services/util";
import { logSessionInfo } from '../../services/logger';


/** Constants **/
// TODO: Run this through translations if / when we start supporting spanish
const DEFAULT_RESPONSES = [
  "Thanks for your thoughts on this!",
  "Appreciate you providing your thoughts.",
  "Thank you for your response!",
]

const NO_INPUT_VOICE_PROMPT = "Oops! I didn't hear you. Let's try again. I'm going to ask you a question and when the microphone turns green, speak with a loud, prod voice to answer!";
const NO_INPUT_TEXT_PROMPT = "Oops! Let's try again.  I'm going to ask you a question and when I am done, please type your answer in the text box."

const NO_INPUT_FINAL_SUBMISSION_RESPONSE = "Thanks for the contribution.";

const NO_SPEAK_TIMEOUT_MS = 10000;
const NO_SUBMIT_TIMEOUT_MS = 60000;
const MAX_WAIT_FOR_SCORED_RESPONSE_MS = 5000;

// These vars alter the behaviour of the component as needed for a staggered release
const LEVELS_OF_DIALOGUE_PER_INTERACTION = 1;
const SHOULD_SPEAK_INFERENCE_RESPONSE = false;
const DETAILED_CONSOLE_LOGGING = false;

const STATES = {
  INITIALIZING: "INITIALIZING",
  START: "START",
  TUTOR_SPEAK_INTRO: "TUTOR_SPEAK_INTRO",
  LISTEN_USER_INPUT: "LISTEN_USER_INPUT",
  HANDLE_NO_INPUT: "HANDLE_NO_INPUT",
  HANDLE_DONE_CLICKED: "HANDLE_DONE_CLICKED",
  TUTOR_SPEAK_RESPONSE: "TUTOR_SPEAK_RESPONSE",
  CLEANUP: "CLEANUP",
}

const Interaction = ({
  speech,
  onDone,
  interactions,
  storyId,
  phraseIndex,
}) => {
  /** Shared State **/
  const { cancellableCPromise } = useCancellablePromise();
  let textResponseInput = createRef();

  const [USE_TEXT_INPUT] = useState(false);
  const [textResponse, setTextResponse] = useState("");
  const textResponseRef = useRef(textResponse); // Step 2: Create ref because of closure issue in timeouts
  const [interactionsLinkedList, setInteractionsLinkedList] = useState(null);
  const [currentInteraction, setCurrentInteraction] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const transcriptRef = useRef(transcript);
  const [promptedForNoInput, setPromptedForNoInput] = useState(false);
  const [submitButtonEnabled, setSubmitButtonEnabled] = useState(false);
  const [listenCompCallbackCPromise, setListenCompCallbackCPromise] = useState(null);
  const [listenCompCallbackData, setListenCompCallbackData] = useState(null);
  const [currentDialogueLevel, setCurrentDialogueLevel] = useState(0);
  const inferenceTimedOutRef = useRef(false);  
  const [state, setState] = useState(STATES.INITIALIZING)


  /** Initialization **/
  useEffect(() => {
    return () => {
      // TODO: If we don't want to save this audio, we can speech.clearAudioDestination before speech.stopListening
      speech.stopListening();
      abortListen(
        speech,
        true // endOfPhrase
      )
    }
  }, []);

  // This effect updates the ref every time textResponse changes
  useEffect(() => {
    textResponseRef.current = textResponse;
  }, [textResponse]);

  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  useEffect(() => {
    if (interactions) {
      // Build the interactions linked list
      setInteractionsLinkedList(interactions.map((interaction, index) => ({...interaction, nextInteraction: interactions[index + 1]})));
    }
  }, [interactions])

  useEffect(() => {
    if (interactionsLinkedList) {
      let firstValidInteraction = interactionsLinkedList[0];
      while (!isInteractionValid(firstValidInteraction)) {
        if (!firstValidInteraction) {
          break;
        }
        firstValidInteraction = firstValidInteraction.nextInteraction;
      }
      if (firstValidInteraction) {
        setCurrentInteraction(firstValidInteraction);
      } else {
        setState(STATES.CLEANUP);
      }
    }
  }, [interactionsLinkedList]);

  useEffect(() => {
    if (currentInteraction) {
      // START THE MACHINE
      setState(STATES.START);
    }
  }, [currentInteraction]);
  
  /** State Machine **/
  useEffect(() => {
    let onExit = () => {};
  
    detailedLog(`COMP2 - ${Date.now().toString()} - Entering State - ${state}`);
    // Current State On Enter
    if (state === STATES.INITIALIZING) return;
    else if (state === STATES.START) onExit = startStateRun();
    else if (state === STATES.TUTOR_SPEAK_INTRO) onExit = tutorSpeakIntroStateRun();
    else if (state === STATES.LISTEN_USER_INPUT) onExit  = listenUserInputStateRun();
    else if (state === STATES.HANDLE_NO_INPUT) onExit = handleNoInputStateRun();
    else if (state === STATES.HANDLE_DONE_CLICKED) onExit = handleDoneClickedStateRun();
    else if (state === STATES.TUTOR_SPEAK_RESPONSE) onExit = tutorSpeakResponseStateRun();
    else if (state === STATES.CLEANUP) onExit = cleanupStateRun();
    
    // State On Exit
    //   Will be called when state changes
    return () => {
      detailedLog(`Interaction - ${Date.now().toString()} - Exiting State - ${state}`);

      onExit();
    }
  }, [state])
  
  // States
  const startStateRun = () => {
    // On Enter
    setTextResponse("");
    setTranscript(null);
    setPromptedForNoInput(false);
    
    // Change State
    setState(STATES.TUTOR_SPEAK_INTRO);
    
    // On Exit
    return () => {};
  }

  const tutorSpeakIntroStateRun = () => {
    let stateHasChanged = false;
    let currentAsyncOperation = null;
    
    // On Enter
    try {
      currentAsyncOperation = speak(currentInteraction.prompt);
      currentAsyncOperation.promise.then(() => {
          // State Change
          setCurrentDialogueLevel(1);
          setState(STATES.LISTEN_USER_INPUT);
        });
    } catch (error) {
      if (!error.isCancelled && !stateHasChanged) throw(error);
    }
    
    // On Exit
    return () => {
      stateHasChanged = true;
      if (currentAsyncOperation) {
        currentAsyncOperation.cancel();
      }
    };
  }

  const listenUserInputStateRun = () => {
    // On Enter
    let stateChanged = false;
    let noInputTimeout, noSubmitTimeout;
    let listenCPromise;
    
    try {
      setListenCompCallbackData(null);
      setListenCompCallbackCPromise(null);
      setSubmitButtonEnabled(true);
      
      if (!promptedForNoInput) {
        // Set no speak timeout
        noInputTimeout = setTimeout(() => {
          if (currentDialogueLevel === 1  && !userInputExists()) {
            // State Change
            setState(STATES.HANDLE_NO_INPUT);
          }
        }, NO_SPEAK_TIMEOUT_MS);
      }

      // Set noSubmit timeout
      noSubmitTimeout = setTimeout(() => {
        // State Change
        setState(STATES.TUTOR_SPEAK_RESPONSE);
      }, NO_SUBMIT_TIMEOUT_MS);

      if (!USE_TEXT_INPUT) {
        startListening();
      }
    } catch (error) {
      if (!error.isCancelled && !stateChanged) throw(error);
    }
    
    // On Exit
    return () => {
      stateChanged = true;
      setSubmitButtonEnabled(false);
      speech.disableRecording(true,{comprehensionDone: true,
        stage: currentDialogueLevel,
        prompt: currentInteraction.prompt,
        questionId: currentInteraction.ids[0],
        storyId,
        phraseIndex: phraseIndex,
      });
      detailedLog(`COMP2 - ${Date.now().toString()} - Stopping listening (signal is sent) - ${state}`);
      speech.stopListening();
      clearTimeout(noInputTimeout);
      clearTimeout(noSubmitTimeout);
    };
  }
  
  const handleNoInputStateRun = () => {
    let stateChanged = false;
    let currentAsyncOperation = null;
    
    // On Enter
    try {
      setPromptedForNoInput(true);
      let textToSpeak;
      if (currentInteraction.noResponsePrompt && currentInteraction.noResponsePrompt !== '') {
        textToSpeak = currentInteraction.noResponsePrompt;
      } else if (USE_TEXT_INPUT) {
        textToSpeak = `${NO_INPUT_TEXT_PROMPT} ${currentInteraction.prompt}`;
      } else {
        textToSpeak = `${NO_INPUT_VOICE_PROMPT} ${currentInteraction.prompt}`;
      }
      currentAsyncOperation = speak(textToSpeak);
      currentAsyncOperation.promise.then(() => {
          // State Change
          setState(STATES.LISTEN_USER_INPUT);
        });
    } catch (error) {
      if (!error.isCancelled && !stateChanged) throw(error);
    }
    
    // On Exit
    return () => {
      stateChanged = true;
      if (currentAsyncOperation) {
        currentAsyncOperation.cancel();
      }
    };
  }

  const handleDoneClickedStateRun = () => {
    let stateChanged = false;
    let currentAsyncOperation = null;
    let timeout;

    
    // On Enter
    try {
      
      // Only prompt the user if they are in first dialogue level.  Otherwise, they have goten through the first succesfully OR they've already gotten the prompt
      if (currentDialogueLevel === 1 && !userInputExists() && !promptedForNoInput) {
        // State Change
        setState(STATES.HANDLE_NO_INPUT);
      } else {
        timeout = setTimeout(() => {
          clearTimeout(timeout);
          logSessionInfo('comprehension_inference_timeout');
          setListenCompCallbackData(null);
          inferenceTimedOutRef.current = true;
          setState(STATES.TUTOR_SPEAK_RESPONSE)
          if (currentAsyncOperation) {
            currentAsyncOperation.cancel();
          }
        }, MAX_WAIT_FOR_SCORED_RESPONSE_MS);

        currentAsyncOperation = listenCompCallbackCPromise;
        currentAsyncOperation.promise.then(data => {
          clearTimeout(timeout);
          setListenCompCallbackData(data);
        }).catch(e => {
          console.error("COMP2: ERROR in comp callback", e)
          clearTimeout(timeout);
          setListenCompCallbackData(null);
        }).finally(() => {
          // State Change
          setState(STATES.TUTOR_SPEAK_RESPONSE)
        });
      }
    } catch (error) {
      if (!error.isCancelled && !stateChanged) throw(error);
    }

    // On Exit
    return () => {
      stateChanged = true;
      if (currentAsyncOperation) {
        currentAsyncOperation.cancel();
      }
    };
  }
  
  const tutorSpeakResponseStateRun = () => {
    let stateChanged = false;
    let currentAsyncOperation = null;

    // On Enter
    try {
      let textToSpeak;
      if (SHOULD_SPEAK_INFERENCE_RESPONSE && listenCompCallbackData && !listenCompCallbackData.timeout && listenCompCallbackData.textToSpeak) {
        detailedLog(`COMP2 - ${Date.now().toString()} - Speaking the listen comp callback response`);
        textToSpeak = listenCompCallbackData.textToSpeak;
      } else if (userInputExists(transcript, textResponse)) {
        detailedLog(`COMP2 - ${Date.now().toString()} - Speaking the default user input exists response`);
        textToSpeak = currentInteraction.response ? currentInteraction.response : DEFAULT_RESPONSES[Math.floor(Math.random() * DEFAULT_RESPONSES.length)];
      } else {
        detailedLog(`COMP2 - ${Date.now().toString()} - Speaking the default user input DOES NOT exist response`);
        textToSpeak = NO_INPUT_FINAL_SUBMISSION_RESPONSE;
      }

      currentAsyncOperation = speak(textToSpeak);
      currentAsyncOperation.promise.then(() => {
        // Handle the next level of dialogue
        if (!inferenceTimedOutRef.current && currentDialogueLevel < LEVELS_OF_DIALOGUE_PER_INTERACTION) {
          setCurrentDialogueLevel(currentDialogueLevel + 1);
          setState(STATES.LISTEN_USER_INPUT);
        }
        // If we are done with levels of dialogue, check for a next interaction OR close out 
        else {
          let nextValidInteraction =  currentInteraction.nextInteraction;
          while (!isInteractionValid(nextValidInteraction)) {
            if (!nextValidInteraction) {
              break;
            }
            nextValidInteraction = nextValidInteraction.nextInteraction;
          }
          if (nextValidInteraction) {
            // State Change (Restart State Machine in currentInteraction useEffect)
            setCurrentInteraction(nextValidInteraction);
          } else {
            // State Change
            setState(STATES.CLEANUP);
          }
        }
      });
    } catch (error) {
      if (!error.isCancelled && !stateChanged) throw(error);
    }

    // On Exit
    return () => {
      stateChanged = true;
      if (currentAsyncOperation) {
        currentAsyncOperation.cancel();
      }
    };
  }
  
  const cleanupStateRun = () => {
    // On Enter (We call on done and we are done.)
    onDone();
    
    // On Exit
    return () => {};
  }

  /** Utility Methods **/
  const speak = (textToSpeak) => {
    return cancellableCPromise(speech.speakWithPromise({ textToSpeak }));
  }

  const listenCallback = (args, newTranscript) => {
    // Don't set transcript back to null if it is not null and newTranscript is null
    if ((transcript && newTranscript) || (!transcript && newTranscript)) {
      setTranscript(newTranscript);
    }
  }

  const isInteractionValid = (interaction) => {
    const valid = interaction && interaction.prompt && interaction.prompt !== '';
    if (!valid) {
      logSentryException(new Error('Story interaction incorrectly configured.'));
    }
    return valid;
  }

  const handleTextResponseChange = (event) => {
    setTextResponse(event.target.value);
  }

  const userInputExists = () => {
    if (!USE_TEXT_INPUT && transcriptRef.current && transcriptRef.current.length) {
      const rawTranscript = transcriptRef.current;

      if (rawTranscript.trim().length) {
        return true;
      }
    }

    if (USE_TEXT_INPUT && textResponseRef.current && textResponseRef.current.length) {
      const rawResponse = textResponseRef.current;

      if (rawResponse.trim().length) {
        return true;
      }
    }

    return false;
  }

  const startListening = () => {
    const promise = new Promise((resolve) => {
      listen({
        callbackOverride: listenCallback,
        speech,
        audioDestination: `comprehension/${currentInteraction.ids[0]}/${currentDialogueLevel}`,
        useComprehension: true,
        storyId,
        phraseIndex: phraseIndex,
        callback: (data) => {
          detailedLog("COMP2: IN CALLBACK. COMP RESPONSE:", data)
          if (!data.error) {
            setListenCompCallbackData(data);
            resolve(data);
          } else {
            setListenCompCallbackData(null);
            resolve(null);
          }
        },
    })});

    const cPromise = cancellableCPromise(promise);
    setListenCompCallbackCPromise(cPromise);
  }

  const detailedLog = (message, data) => {
    if (DETAILED_CONSOLE_LOGGING) {
      console.log(message, data);
    }
  }

  /** Render **/
  return (
    <div className="Interaction">
      {currentInteraction && (
        <Fragment>
          <div className="prompt" {...doNotUseAccessibleStaticElementInteractions({
            onClick: () => {
            }
          })}>
            {currentInteraction.prompt}
          </div>
          {currentInteraction.imageURL && <img src={currentInteraction.imageURL} alt=""/>}
          <div className="controlContainer fixedInPlace">
            {USE_TEXT_INPUT && (
              <div className={"text-wrapper"}>
                <FormField key={'comprehension-text-response'} theme={AmiraTheme}>
                  <InputLabel theme={AmiraTheme}>
                    Enter Response
                  </InputLabel>
                  <textarea
                    className={`${AmplifyUI.textarea} text-response-input`}
                    style={{fontSize: 16}}
                    name={"amiraTextResponse"}
                    ref={el => textResponseInput = el}
                    autoFocus={true}
                    placeholder="Enter response here"
                    onChange={handleTextResponseChange}
                    value={textResponse}
                    disabled={!submitButtonEnabled}
                  />
                </FormField>
              </div>
            )}
            {!USE_TEXT_INPUT && (
              <div className="listeningStatusWrapper" style={{marginTop: '20px'}}><ListeningStatus/></div>
            )}
            <Button onClick={() => setState(STATES.HANDLE_DONE_CLICKED)} type="text" disabled={!submitButtonEnabled}>Done</Button>
          </div>
        </Fragment>
      )}
    </div>
  )
}

export default Interaction;